import { defineComponent } from 'vue'
import type { DialogServiceMethods } from 'primevue/dialogservice'
import Button from 'primevue/button'
import { createDeferred } from '@/utils/deferred'
import { promptScheduleWalkthrough } from '@/components/schedule-walkthrough-dialog'
import {
  TOUR_INVITATION,
  TOUR_MOBILE_NOTICE,
  type TourVariant
} from '@/components/tour/console-tour-steps'
import { resolveTourSteps } from '@/composables/useConsoleTour'
import '@/components/support-dialog.css'
import '@/components/tour/console-tour.css'

export type TourInvitationAnswer = 'accept' | 'decline' | 'unanswered'

export interface TourInvitationOptions {
  variant: TourVariant
  /**
   * True when the viewport is too narrow to run the tour. The invitation becomes an explanation
   * plus the live-walkthrough offer, rather than a button that would highlight collapsed chrome.
   */
  narrowScreen?: boolean
}

/**
 * Asks whether the person wants the console tour, before anything is highlighted.
 *
 * Asking first is the point. An unrequested overlay that seizes the screen the moment someone
 * signs in to do a job reads as an obstacle no matter how good the content is, and the people
 * most likely to need the tour are the ones most likely to be mid-task. It also gives "no" a
 * real home, which is what makes a one-time offer honest.
 *
 * Resolves 'accept' to run the tour, 'decline' to never offer it again, and 'unanswered' when
 * they closed the dialog without choosing -- which leaves the offer open for another day.
 */
export function promptConsoleTourInvitation (
  dialog: DialogServiceMethods,
  options: TourInvitationOptions
): Promise<TourInvitationAnswer> {
  const deferred = createDeferred<TourInvitationAnswer>()
  const copy = TOUR_INVITATION[options.variant]
  // Counted from the anchors actually on the page, so the promise matches the tour. Skipped on
  // a narrow screen, where the tour will not run and the count is not shown.
  const stepCount = options.narrowScreen ? 0 : resolveTourSteps(options.variant).length

  // `deferred.isPending` only flips on a microtask, so closing the dialog from `finish` would
  // still look pending to the onClose handler below. Track the answer ourselves instead.
  let answered = false

  function finish (answer: TourInvitationAnswer) {
    if (!answered) {
      answered = true
      deferred.resolve(answer)
    }
    dialogRef.close()
  }

  const DialogContent = defineComponent({
    setup () {
      return () => (
        <div class="support-dialog console-tour-invitation">
          <div class="feedback-step feedback-step-narrow">
            <div class="tw:text-base tw:font-semibold tw:mb-2">{copy.title}</div>
            <div class="tw:text-sm tw:mb-4">
              { options.narrowScreen ? TOUR_MOBILE_NOTICE : copy.body }
            </div>

            { options.narrowScreen
              ? <div class="tw:flex tw:justify-end tw:gap-2">
                <Button
                  type="button"
                  size="small"
                  severity="secondary"
                  label="Close"
                  onClick={() => finish('unanswered')}
                />
                <Button
                  type="button"
                  size="small"
                  label="Book a walkthrough"
                  onClick={() => {
                    finish('unanswered')
                    promptScheduleWalkthrough(dialog)
                  }}
                />
              </div>
              : <div class="tw:flex tw:items-center tw:justify-between tw:gap-2">
                <span class="console-tour-step-count">{stepCount} steps, about a minute</span>
                <div class="tw:flex tw:gap-2">
                  <Button
                    type="button"
                    size="small"
                    severity="secondary"
                    label={copy.decline}
                    onClick={() => finish('decline')}
                  />
                  <Button
                    type="button"
                    size="small"
                    label={copy.accept}
                    onClick={() => finish('accept')}
                  />
                </div>
              </div>
            }
          </div>
        </div>
      )
    }
  })

  const dialogRef = dialog.open(DialogContent, {
    props: {
      header: copy.header,
      pt: {
        root: 'support-dialog-shell tw:!overflow-hidden tw:!rounded-xl'
      },
      modal: true,
      closable: true,
      dismissableMask: true,
      closeOnEscape: true
    },
    // Closing without choosing is not a "no" -- they may just be busy, so the offer stays open.
    onClose: () => {
      if (!answered) {
        answered = true
        deferred.resolve('unanswered')
      }
    }
  })

  return deferred.promise
}
